import { Edit, Play, Settings, Wallet } from 'lucide-react'
import { createElement } from 'react'
import { v4 as uuidv4 } from 'uuid'
import type { AppCommandItem, NavigateProps } from './types'

interface FlowListCommandsProps extends NavigateProps {
  canCreateAgent: boolean
}

export const getFlowListCommands = ({
  navigate,
  canCreateAgent,
}: FlowListCommandsProps): AppCommandItem[] => [
  ...(canCreateAgent
    ? [
        {
          id: 'flow-list-create-agent',
          label: 'Create Agent',
          icon: createElement(Wallet, { className: 'w-4 h-4' }),
          group: 'Agents',
          action: () => navigate(`/agent/builder/${uuidv4()}?new`),
        },
      ]
    : []),
]

interface FlowBuilderCommandsProps {
  isEditing: boolean
  // True while the on-page lifecycle actions are disabled (viewing a historical version, or
  // the flow is compiler-locked) — Publish/Edit must drop out of the palette and shortcuts
  // together with the on-page button, not just visually.
  isDisabled?: boolean
  // Whether the user has aiAgent edit permission. Publish/Edit are write actions, so for a
  // read-only user they're omitted entirely rather than shown as a no-op (their on-page
  // ButtonWithPermission renders null, so the command would just click nothing).
  canEdit?: boolean
}

export const getFlowBuilderCommands = ({
  isEditing,
  isDisabled = false,
  canEdit = true,
}: FlowBuilderCommandsProps): AppCommandItem[] => {
  const commands: AppCommandItem[] = []

  // Flow management commands — omitted entirely while disabled or without edit permission, so
  // neither the palette entry nor its keyboard shortcut can invoke a lifecycle action the
  // on-page button blocks.
  if (canEdit && !isDisabled && isEditing) {
    commands.push({
      id: 'flow-builder-publish',
      label: 'Publish',
      icon: createElement(Play, { className: 'w-4 h-4' }),
      group: 'Flow Management',
      realShortcut: { key: 'p', meta: true, shift: false },
      onlyOnHover: true,
      action: () => {
        if (isDisabled) return
        const publishButton = document.querySelector(
          '[data-testid="button-publish-flow"]',
        ) as HTMLButtonElement
        if (publishButton && !publishButton.disabled) {
          publishButton.click()
        }
      },
    })
  } else if (canEdit && !isDisabled) {
    commands.push({
      id: 'flow-builder-edit',
      label: 'Edit',
      icon: createElement(Edit, { className: 'w-4 h-4' }),
      group: 'Flow Management',
      realShortcut: { key: 'e', meta: true, shift: false },
      onlyOnHover: true,
      action: () => {
        if (isDisabled) return
        const editButton = document.querySelector(
          '[data-testid="button-edit-flow"]',
        ) as HTMLButtonElement
        if (editButton && !editButton.disabled) {
          editButton.click()
        }
      },
    })
  }

  commands.push({
    id: 'flow-builder-agent-settings',
    label: 'Agent settings',
    icon: createElement(Settings, { className: 'w-4 h-4' }),
    group: 'Flow Management',
    action: () => {
      const settingsButton = document.querySelector(
        '[data-testid="button-knowledge-base"]',
      ) as HTMLButtonElement
      if (settingsButton && !settingsButton.disabled) {
        settingsButton.click()
      }
    },
  })

  return commands
}
